import { ReactNode } from 'react';
import { Badge } from '@crisellevaldez/speculo-ui';
import { CheckCircle, Globe, Mail, TrendingUp, UserCircle, XCircle } from 'lucide-react';
import { HiGlobe, HiSearch } from 'react-icons/hi';
import { HiDevicePhoneMobile, HiComputerDesktop } from 'react-icons/hi2';
import { formatPhoneNumber } from '../../utils/formatters';
import { formatTemplateName } from '../../utils/formatters/templateFormatters';
import { previewEmail } from '../../utils/email/previewEmail';
import { stripTrackingParams } from '../../utils/urlUtils';
import {
  getIntentStyle,
  IntentType,
  getLeadSourceStyle,
  getWebActivityEventTypeStyle,
} from '../../utils/badgeStyles';
import ScrollableCell from '../common/ScrollableCell';
import { formatOptInDate } from '../../utils/dateTime';
import type { FormattedUser } from '../../hooks/graphql/users/useOrganizationUsers';
import { AssignedAgentCell } from '../common/AssignedAgentCell';
import { NoWrapCell } from '../../utils/tableCells';

interface LeadItem {
  id: string;
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  dateTime?: string;
  intent?: string;
  valuationRequested?: unknown[];
  leadSource?: string;
  leadSourceSecondary?: string;
  conversionSource?: string;
  claimedLead?: { name?: string; phoneNumber?: string };
  assignedAgentId?: string | null;
  assignedPondId?: string | null;
  assignedAgent?: {
    agentId?: string;
    firstName?: string;
    lastName?: string;
    email?: string | null;
    phone?: string | null;
    photoUrl?: string | null;
  } | null;
}

interface EmailLogItem {
  id: string;
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  dateTime?: string;
  templateName?: string;
  bodyHtml?: string;
  subjectLine?: string;
  opens?: number;
}

interface WebActivityItem {
  id: string;
  userId?: string;
  fullName?: string;
  phoneNumber?: string;
  email?: string;
  deviceType?: string;
  pageType?: string;
  pageUrl?: string;
  pageTitle?: string;
  eventCategory?: string;
  dateTime?: string;
  properties?: {
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    searchQuery?: string;
  };
  guest?: {
    contactId?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
  };
}

export function getLeadsColumns(
  handleOpenEditModal: (contact: { id: string }) => void,
  timezone?: string,
  orgUsers: FormattedUser[] = []
) {
  return [
    {
      key: 'fullName',
      header: 'Name',
      width: '200px',
      sortable: true,
      cell: (item: LeadItem) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleOpenEditModal(item);
          }}
          className="text-primaryfortext font-medium underline text-left"
        >
          {item.fullName}
        </button>
      ),
    },
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      width: '150px',
      cell: (item: LeadItem) => formatPhoneNumber(item.phoneNumber),
    },
    { key: 'email', header: 'Email', width: '200px' },
    {
      key: 'dateTime',
      header: 'Opt In Date',
      width: '160px',
      sortable: true,
      cell: (item: LeadItem) => <NoWrapCell>{formatOptInDate(item.dateTime) ?? '-'}</NoWrapCell>,
    },
    {
      key: 'intent',
      header: 'Intent',
      width: '100px',
      sortable: true,
      cell: (item: LeadItem) => {
        if (!item.intent || item.intent === '')
          return <span className="text-muted-foreground">-</span>;
        const styles = getIntentStyle(item.intent as IntentType);
        return (
          <Badge className={`${styles.background} ${styles.text}`}>{item.intent}</Badge>
        );
      },
    },
    {
      key: 'leadSourceSecondary',
      header: 'Lead Source',
      width: '150px',
      sortable: false,
      cell: (item: LeadItem) => {
        const value = item.leadSourceSecondary || '';
        if (!value) return <span className="text-muted-foreground">-</span>;
        const knownSources = ['Email', 'PPC', 'Organic'];
        if (!knownSources.includes(value)) {
          return <span className="text-xs text-foreground">{value}</span>;
        }
        const style = getLeadSourceStyle(value);
        let icon;
        if (value === 'Email') icon = <Mail className="w-3 h-3 mr-1" />;
        else if (value === 'PPC') icon = <TrendingUp className="w-3 h-3 mr-1" />;
        else if (value === 'Organic') icon = <Globe className="w-3 h-3 mr-1" />;
        return (
          <Badge className={`${style.background} ${style.text} gap-1`}>
            {icon}
            {value}
          </Badge>
        );
      },
    },
    {
      key: 'leadSource',
      header: 'Source',
      width: '160px',
      sortable: false,
      cell: (item: LeadItem) => {
        if (!item.leadSource || item.leadSource === '')
          return <span className="text-muted-foreground">-</span>;
        return <span className="text-xs text-foreground">{item.leadSource}</span>;
      },
    },
    {
      key: 'conversionSource',
      header: 'Conversion Source',
      width: '250px',
      cell: (item: LeadItem) => {
        const cleanedUrl = stripTrackingParams(item.conversionSource ?? '');
        return (
          <ScrollableCell>
            <a
              href={cleanedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primaryfortext hover:text-primary/80 underline"
            >
              {cleanedUrl}
            </a>
          </ScrollableCell>
        );
      },
    },
    {
      key: 'assignedAgent',
      header: 'Assigned To',
      sortable: false,
      width: '200px',
      cell: (item: LeadItem) => (
        <AssignedAgentCell
          agent={item.assignedAgent as never}
          agentId={item.assignedAgentId}
          pondId={item.assignedPondId}
          orgUsers={orgUsers}
        />
      ),
    },
  ];
}

export function getEmailOpensColumns(handleOpenEditModal: (contact: { id: string }) => void) {
  return [
    {
      key: 'fullName',
      header: 'Name',
      width: '200px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item: EmailLogItem) => {
        if (item.fullName === '-') return <span className="text-muted-foreground">-</span>;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(item);
            }}
            className="text-primaryfortext font-medium underline text-left"
          >
            {item.fullName}
          </button>
        );
      },
    },
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      width: '150px',
      cell: (item: EmailLogItem) => formatPhoneNumber(item.phoneNumber),
    },
    { key: 'email', header: 'Email', width: '200px' },
    {
      key: 'dateTime',
      header: 'Date/Time',
      width: '220px',
      sortable: true,
      cell: (item: EmailLogItem) => <NoWrapCell>{item.dateTime}</NoWrapCell>,
    },
    {
      key: 'templateName',
      header: 'Template',
      width: '200px',
      cell: (item: EmailLogItem) => (
        <ScrollableCell>
          {!item.templateName || item.templateName === '' || item.templateName === '-' ? (
            item.bodyHtml ? (
              <button
                onClick={() =>
                  previewEmail({
                    bodyHtml: item.bodyHtml,
                    subject: item.subjectLine || 'New Property Matches Found',
                    templateName: item.templateName || undefined,
                    contactName: item.fullName,
                    contactEmail: item.email,
                    toAddress: item.email,
                  })
                }
                className="text-primaryfortext hover:text-primaryfortext/80 underline text-left font-medium"
              >
                E Alert - {item.subjectLine || 'New Property Matches Found'}
              </button>
            ) : (
              <span className="text-foreground">E Alert - New Property Matches Found</span>
            )
          ) : item.bodyHtml ? (
            <button
              onClick={() =>
                previewEmail({
                  bodyHtml: item.bodyHtml,
                  subject: item.subjectLine || undefined,
                  templateName: item.templateName,
                  contactName: item.fullName,
                  contactEmail: item.email,
                  toAddress: item.email,
                })
              }
              className="text-primaryfortext hover:text-primaryfortext/80 underline text-left font-medium"
            >
              {`${formatTemplateName(item.templateName)} - ${item.subjectLine || ''}`}
            </button>
          ) : (
            <span className="text-foreground">{item.templateName}</span>
          )}
        </ScrollableCell>
      ),
    },
    { key: 'opens', header: 'No. of Opens', width: '120px', isCentered: true },
  ];
}

export function getWebsiteColumns(handleOpenEditModal: (contact: { id: string }) => void) {
  return [
    {
      key: 'loggedInStatus',
      header: 'Logged-In',
      width: '54px',
      isCentered: true,
      cell: (item: WebActivityItem) => (
        <div className="flex justify-center items-center">
          {item.userId ? (
            <div title="Logged In">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
          ) : (
            <div title="Guest">
              <XCircle className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'fullName',
      header: 'Name',
      width: '280px',
      isPinned: true,
      pinPosition: 'left',
      cell: (item: WebActivityItem) => {
        const isGuest = !item.userId;
        let displayName = item.fullName;
        let contactData: { id: string } = { id: item.id };
        if (isGuest && item.guest) {
          const guestName = `${item.guest.firstName || ''} ${item.guest.lastName || ''}`.trim();
          if (guestName) displayName = guestName;
          contactData = { id: item.guest.contactId || item.id };
        }
        if (!displayName || displayName === '-') {
          if (isGuest) return <Badge className="bg-gray-200 text-gray-700">Guest</Badge>;
          return <span className="text-muted-foreground">-</span>;
        }
        const hasGuestData =
          isGuest &&
          item.guest &&
          (item.guest.firstName ||
            item.guest.lastName ||
            item.guest.phoneNumber ||
            item.guest.email);
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditModal(contactData);
            }}
            className="text-foreground font-medium text-left flex items-center gap-2"
          >
            {isGuest && (
              <Badge
                className={`${hasGuestData ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'} items-center gap-2`}
              >
                {hasGuestData ? <Mail className="w-3 h-3" /> : <UserCircle className="w-3 h-3" />}
                {hasGuestData ? 'from eAlert' : 'Guest'}
              </Badge>
            )}
            <span className="underline">{displayName}</span>
          </button>
        );
      },
    },
    {
      key: 'phoneNumber',
      header: 'Phone Number',
      width: '150px',
      cell: (item: WebActivityItem) => {
        if (!item.userId && item.guest?.phoneNumber)
          return formatPhoneNumber(item.guest.phoneNumber);
        return formatPhoneNumber(item.phoneNumber);
      },
    },
    {
      key: 'email',
      header: 'Email',
      width: '200px',
      cell: (item: WebActivityItem) => {
        if (!item.userId && item.guest?.email) return item.guest.email;
        return item.email || '-';
      },
    },
    {
      key: 'deviceType',
      header: 'Device',
      width: '100px',
      cell: (item: WebActivityItem) => {
        const map: Record<string, { label: string; icon: ReactNode }> = {
          mobile: {
            label: 'Mobile',
            icon: <HiDevicePhoneMobile className="h-5 w-5 text-primary" />,
          },
          desktop: {
            label: 'Desktop',
            icon: <HiComputerDesktop className="h-5 w-5 text-primary" />,
          },
        };
        const info = map[item.deviceType ?? ''] || {
          label: item.deviceType || 'Unknown',
          icon: <HiGlobe className="h-5 w-5 text-primary" />,
        };
        return (
          <div className="flex items-center gap-2">
            {info.icon}
            <span className="text-sm">{info.label}</span>
          </div>
        );
      },
    },
    {
      key: 'pageUrl',
      header: 'Page URL',
      width: '300px',
      cell: (item: WebActivityItem) => {
        const cleanedUrl = stripTrackingParams(item.pageUrl ?? '');
        const isPropertyEvent = ['property_view', 'property_saved', 'lead_capture'].includes(
          item.pageType ?? ''
        );
        const isSearchEvent = item.pageType === 'search';
        let propertyInfo = '';
        if (isPropertyEvent && item.properties) {
          const { address, city, state, zipCode } = item.properties;
          if (address && city)
            propertyInfo = `${address}, ${city}${state ? `, ${state}` : ''}${zipCode ? ` ${zipCode}` : ''}`;
          else if (item.pageTitle) propertyInfo = item.pageTitle;
        }
        let searchLabel = '';
        if (isSearchEvent && item.properties?.searchQuery)
          searchLabel = item.properties.searchQuery;
        else if (isSearchEvent && item.pageTitle)
          searchLabel = item.pageTitle.replace('Search Results: ', '');
        return (
          <ScrollableCell>
            {isPropertyEvent && propertyInfo ? (
              <a
                href={cleanedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primaryfortext hover:text-primaryfortext/80 underline"
              >
                {propertyInfo}
              </a>
            ) : isSearchEvent && searchLabel ? (
              <a
                href={cleanedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primaryfortext hover:text-primaryfortext/80 underline flex items-center gap-1.5"
              >
                <HiSearch className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">{searchLabel}</span>
              </a>
            ) : (
              <a
                href={cleanedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primaryfortext hover:text-primaryfortext/80 underline"
              >
                {cleanedUrl}
              </a>
            )}
          </ScrollableCell>
        );
      },
    },
    {
      key: 'pageType',
      header: 'Event Type',
      width: '200px',
      cell: (item: WebActivityItem) => {
        const eventCategory = item.eventCategory || '';
        let style;
        if (item.pageType === 'property_view') {
          style =
            item.pageUrl?.includes('valuation') || eventCategory === 'Valuation'
              ? { background: 'bg-[#D1FAF5]', text: 'text-[#0F766E]' }
              : { background: 'bg-[#FBD5E0]', text: 'text-[#9D174D]' };
        } else {
          style = getWebActivityEventTypeStyle(item.pageType ?? '', eventCategory);
        }
        let label = item.pageType
          ? item.pageType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())
          : 'Unknown';
        if (item.pageType === 'property_view') {
          label =
            eventCategory === 'Valuation' || item.pageUrl?.includes('valuation')
              ? 'Home Valuation - Seller'
              : 'Property View - Buyer';
        }
        return <Badge className={`${style.background} ${style.text}`}>{label}</Badge>;
      },
    },
    {
      key: 'dateTime',
      header: 'Date/Time',
      width: '220px',
      cell: (item: WebActivityItem) => <NoWrapCell>{item.dateTime}</NoWrapCell>,
    },
  ];
}
